import { useState } from 'react'
import { useParams } from 'react-router-dom'

import { F0Dialog } from '@factorialco/f0-react'
import { ProcessTargetDataTableDocument } from 'generated/resources/datatable.graphql'
import {
  AddTargetsDocument,
  AddTargetsMutation,
  EmployeeForSelectFragment,
  EmployeesEmployeeSelectorInput,
  PerformanceReviewOverviewHeaderDocument,
  PerformanceTargetManagementDocument,
} from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { EmployeeSelectorV2 } from 'modules/employees/components/EmployeeSelectorV2'

interface AddTargetsModalProps {
  onClose: () => void
  alreadySelectedTargets: EmployeeForSelectFragment[]
}

export const AddTargetsModal = ({ onClose, alreadySelectedTargets }: AddTargetsModalProps) => {
  const { reviewId } = useParams()
  const [selectorEmployees, setSelectorEmployees] = useState<EmployeesEmployeeSelectorInput>({
    type: 'manual',
    ids: [],
  })
  const [addTargets, { loading }] = useMutation(AddTargetsDocument, {
    extractorFn: (data: AddTargetsMutation) =>
      data.performanceMutations.bulkCreatePerformanceReviewProcessTarget,
  })

  const onSubmit = async () => {
    const response = await addTargets({
      awaitRefetchQueries: true,
      variables: {
        performanceReviewProcessId: reviewId ?? '',
        employeeSelection: selectorEmployees,
      },
      refetchQueries: [
        ProcessTargetDataTableDocument,
        PerformanceTargetManagementDocument,
        PerformanceReviewOverviewHeaderDocument,
      ],
    })

    const handled = handleResult(response, () => undefined)

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      onClose()
    }
  }

  return (
    <F0Dialog
      isOpen
      width="sm"
      onClose={() => {
        if (loading) return
        onClose()
      }}
      title={i18n.t('performance_process.reviews.target_management_addition_title')}
      description={i18n.t('performance_process.reviews.target_management_addition_description')}
      primaryAction={{
        label: i18n.t('common.add'),
        onClick: onSubmit,
        disabled: loading,
        loading: loading,
      }}
      secondaryAction={{
        label: i18n.t('common.cancel'),
        onClick: onClose,
      }}
    >
      <EmployeeSelectorV2
        multiple
        forcedFilters={{
          excludeIds: alreadySelectedTargets.map((target) => target.id),
        }}
        onChange={(state) => setSelectorEmployees(state)}
        messages={{
          placeholder: i18n.t('employees.select_employee.other'),
        }}
        label={i18n.t('performance_process.reviews.select_targets_label')}
      />
    </F0Dialog>
  )
}
